// The flagged-RFQ priority board — a drag-ordered focus list.
//
// Deliberately minimal. No swimlanes, no assignees, no statuses beyond what an
// RFQ already has. It answers exactly one question: "what am I working on, in
// what order." Anything more and it becomes a second system to maintain
// alongside the pipeline that already exists on rfq_line_items.stage.
//
// Ordering is sparse fractional ranking (shared/rank.ts), so a drag is ONE row
// write regardless of list length rather than renumbering everything below the
// insertion point.

import { useMemo, useState, type ReactNode } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DndContext, closestCenter, KeyboardSensor, MouseSensor, TouchSensor,
  useSensor, useSensors, type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext, sortableKeyboardCoordinates, useSortable,
  verticalListSortingStrategy, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { api, type RfqListItem, type RfqListResponse } from '@/lib/api';
import { RfqRow } from '@/components/rfq-row';
import { FlagToggle } from '@/components/flag-toggle';
import { useToggleRfqFlag, BOARD_QUERY_KEY } from '@/lib/use-toggle-rfq-flag';
import { computeRankForMove, rebalanceRanks } from '@shared/rank';

interface Props {
  selectedRfqId: string | null;
}

/** Matches BOARD_MAX in server/routes/rfqs.ts. */
const BOARD_MAX = 200;

// Re-exported from the toggle hook rather than declared twice. The hook edits
// this cache directly on unflag, so two literals that could drift apart is
// exactly the bug worth designing out.
const QUERY_KEY = BOARD_QUERY_KEY;

function SortableRfq({
  rfq,
  isSelected,
  flagToggle,
}: {
  rfq: RfqListItem;
  isSelected: boolean;
  /** Owned by RfqBoard — SortableRfq stays presentational, like RfqRow. */
  flagToggle?: ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: rfq.id });

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={isDragging ? 'opacity-50 relative z-10' : undefined}
    >
      <RfqRow
        rfq={rfq}
        isSelected={isSelected}
        dragHandle={
          // Listeners live on the HANDLE, not the row: the row is a link, and a
          // whole-row drag sensor would swallow the click that opens the RFQ.
          //
          // Sizing is a touch requirement, not decoration. ui-conventions.md §8
          // asks for >=40px targets, and a drag handle is the worst possible
          // place to be stingy — a missed grab on a phone either scrolls the
          // list or opens the RFQ, both of which feel broken.
          //
          // touch-none is what stops the browser claiming the gesture as a
          // scroll before dnd-kit ever sees it; it is scoped to the handle so
          // the rest of the row still scrolls normally.
          <button
            type="button"
            {...attributes}
            {...listeners}
            onClick={(e) => e.preventDefault()}
            aria-label={`Reorder ${rfq.ref}`}
            title="Drag to reorder"
            className="shrink-0 flex items-center justify-center self-stretch
                       min-w-[40px] min-h-[40px] text-muted-foreground
                       hover:text-foreground hover:bg-muted/50 cursor-grab
                       active:cursor-grabbing touch-none select-none
                       focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            ⠿
          </button>
        }
        flagToggle={flagToggle}
      />
    </li>
  );
}

export function RfqBoard({ selectedRfqId }: Props) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () =>
      api<RfqListResponse>('/api/rfqs?flagged=true&board=true', {
        tokenGetter: () => getToken(),
      }),
    staleTime: 10_000,
  });

  // The server already returns board order; trust it rather than re-sorting, so
  // the list cannot disagree with what the next refetch will produce.
  const rfqs = useMemo(() => data?.rfqs ?? [], [data]);

  const toggleFlag = useToggleRfqFlag();

  const persistRank = useMutation({
    mutationFn: (vars: { publicId: string; boardRank: number }) =>
      api(`/api/rfqs/${vars.publicId}`, {
        method: 'PATCH',
        tokenGetter: () => getToken(),
        body: JSON.stringify({ boardRank: vars.boardRank }),
      }),
  });

  const persistRebalance = useMutation({
    mutationFn: (orderedPublicIds: string[]) =>
      api('/api/rfqs/board/rebalance', {
        method: 'POST',
        tokenGetter: () => getToken(),
        body: JSON.stringify({ orderedPublicIds }),
      }),
  });

  const sensors = useSensors(
    // MouseSensor + TouchSensor rather than PointerSensor, because the two
    // input types want DIFFERENT activation rules and PointerSensor can only
    // express one:
    //   - mouse: a small distance threshold, so a click on the handle stays a
    //     click instead of starting a zero-length drag.
    //   - touch: a short press-and-hold. A distance threshold on touch fights
    //     the scroll gesture — every attempt to scroll the list with a finger
    //     that happens to start on a handle would begin dragging instead.
    // The tolerance lets a finger wobble a few pixels during the hold without
    // cancelling, which matters on a phone held one-handed.
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  async function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const fromIndex = rfqs.findIndex((r) => r.id === active.id);
    const toIndex = rfqs.findIndex((r) => r.id === over.id);
    if (fromIndex < 0 || toIndex < 0) return;

    const moved = rfqs[fromIndex]!;
    const { rank, rebalance } = computeRankForMove(
      rfqs.map((r) => r.boardRank),
      fromIndex,
      toIndex,
    );

    // Cancel any in-flight refetch FIRST. staleTime is 10s, so a background
    // refetch can land mid-drag and overwrite the optimistic order with the
    // pre-drag server state — the row visibly snaps back even though the write
    // succeeded. This is the standard TanStack optimistic-update guard and it
    // is easy to omit precisely because it only bites under a race.
    await queryClient.cancelQueries({ queryKey: QUERY_KEY });

    // Optimistic reorder. Without it the row snaps back to its old slot for the
    // duration of the round trip, which reads as "the drag failed".
    const reordered = arrayMove(rfqs, fromIndex, toIndex);
    const optimistic = reordered.map((r) =>
      r.id === moved.id ? { ...r, boardRank: rank } : r,
    );
    queryClient.setQueryData<RfqListResponse>(QUERY_KEY, (old) =>
      old ? { ...old, rfqs: optimistic } : old,
    );
    setError(null);

    try {
      await persistRank.mutateAsync({ publicId: moved.id, boardRank: rank });

      // Rare: the gap this landed in is too narrow to keep subdividing. Respace
      // the whole board so the NEXT drag has room. Done after the move lands so
      // a rebalance failure cannot lose the move itself.
      if (rebalance) {
        const order = optimistic.map((r) => r.id);
        await persistRebalance.mutateAsync(order);
        const respaced = new Map(rebalanceRanks(order).map((x) => [x.id, x.rank]));
        queryClient.setQueryData<RfqListResponse>(QUERY_KEY, (old) =>
          old
            ? { ...old, rfqs: old.rfqs.map((r) => ({ ...r, boardRank: respaced.get(r.id) ?? r.boardRank })) }
            : old,
        );
      }
    } catch (e) {
      // Roll the optimistic order back to the truth rather than leaving the UI
      // showing an order the server never accepted.
      setError((e as { message?: string })?.message ?? 'Could not save the new order');
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    }
  }

  if (isLoading) return <div className="p-6 text-sm text-muted-foreground">Loading board…</div>;
  if (isError) return <div className="p-6 text-sm text-destructive">Failed to load the board.</div>;

  if (rfqs.length === 0) {
    return (
      <div className="p-6 text-sm text-muted-foreground">
        Nothing flagged. Flag an RFQ from its detail pane to add it here, then drag to prioritise.
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto">
      {error && (
        // Not .pill here — .pill is inline-flex with a pill radius, which fights
        // a full-width message block.
        <div
          role="alert"
          className="mx-4 mt-3 px-3 py-2 text-xs rounded-md
                     bg-[hsl(var(--danger-muted))] text-[hsl(var(--danger))]"
        >
          {error}
        </div>
      )}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={rfqs.map((r) => r.id)} strategy={verticalListSortingStrategy}>
          <ul>
            {rfqs.map((rfq) => (
              <SortableRfq
                key={rfq.id}
                rfq={rfq}
                isSelected={rfq.id === selectedRfqId}
                flagToggle={
                  // Always flagged here — board membership IS the flag — so this
                  // is only ever "take it off my board".
                  <FlagToggle
                    isFlagged
                    isPending={toggleFlag.isPending && toggleFlag.variables?.id === rfq.id}
                    isError={toggleFlag.isError && toggleFlag.variables?.id === rfq.id}
                    onToggle={() => toggleFlag.mutate({ id: rfq.id, isFlagged: false })}
                  />
                }
              />
            ))}
          </ul>
        </SortableContext>
      </DndContext>
      <div className="p-3 text-center text-xs text-muted-foreground">
        {rfqs.length} flagged · drag ⠿ to reorder
        {/* Truncation is stated, never silent — a board that quietly stopped at
            200 would read as "that's everything". */}
        {rfqs.length >= BOARD_MAX && ` · showing the first ${BOARD_MAX}`}
      </div>
    </div>
  );
}
